import React, { FC, KeyboardEventHandler, useRef } from "react";
import { Box, Button, Flex, Heading, Text, Link } from "@chakra-ui/react";
import NextLink from "next/link";
import { MotionBox } from "utils/framer/motionbox";
import { MainNavigationFieldsProps, getNavigationText } from ".";
import { useRouter } from "next/router";
import BundleIcon from "components/ui/BundleIcon";
import { useI18n } from "next-localization";

type SubNavigationLevelProps = {
    Children?: MainNavigationFieldsProps[];
    index: number;
    parentItem?: MainNavigationFieldsProps;
    open: boolean;
    disclosureProps: any; // eslint-disable-line
    closeSubNav: () => void;
    handleLevelChange: (level: number, Children: MainNavigationFieldsProps | null) => void;
    activeIndex: number;
    activeMenuData: { level: number; fields: MainNavigationFieldsProps }[];
    isLgBreakpointOrAbove: boolean;
    subNavItemIndex?: number;
};

const SubNavigationLevel: FC<SubNavigationLevelProps> = ({
    Children,
    index,
    parentItem,
    open,
    disclosureProps,
    handleLevelChange,
    closeSubNav,
    activeIndex,
    activeMenuData,
    isLgBreakpointOrAbove,
    subNavItemIndex,
}) => {
    const { t } = useI18n();
    const router = useRouter();
    const parentRef = useRef<HTMLAnchorElement>(null);

    const motionVariantsStyle = isLgBreakpointOrAbove
        ? { flexShrink: 1, x: 0 }
        : { flexShrink: 0, x: "-100%" };

    const motionVariants = {
        open: {
            opacity: 1,
            x: 0,
            display: "flex",
            flexShrink: 1,
        },
        closed: {
            opacity: 0,
            ...motionVariantsStyle,
            transitionEnd: {
                display: "none",
            },
        },
    };

    const transition = {
        default: {
            ease: [0.35, 1, 0.45, 1],
            duration: isLgBreakpointOrAbove ? 0.25 : 0.5,
            delay: 0.05,
        },
    };

    const navigateBack = () => {
        if (index !== null) {
            handleLevelChange(index - 1, null);
        }
    };

    const onKeyDown: KeyboardEventHandler<HTMLAnchorElement> = (e) => {
        if (e.shiftKey && e.key === "Tab") {
            e.preventDefault();
            getParentElement();
        }
    };

    let levelClass = "";

    if ((isLgBreakpointOrAbove && index === 0) || (!isLgBreakpointOrAbove && index === 1)) {
        levelClass = " second-level";
    } else if ((isLgBreakpointOrAbove && index === 1) || (!isLgBreakpointOrAbove && index === 2)) {
        levelClass = " third-level";
    }

    const navigationText = t(`navigationLevelOne_${subNavItemIndex}`);

    const getParentElement = () => {
        const parentElement: HTMLAnchorElement | null = document.querySelector(
            `[aria-controls='${parentItem?.Id}']`
        );
        parentElement?.focus();
    };

    const renderBackButton = () => {
        if (parentItem) {
            return (
                <Button
                    className="backbutton"
                    variant={"unstyled"}
                    onClick={navigateBack}
                    leftIcon={
                        <BundleIcon
                            name="ArrowBackIos"
                            size={["sm", null, null, "md"]}
                            fill="black"
                        />
                    }
                >
                    {t("mobileNavigationBackButtonText")}
                </Button>
            );
        }
        return null;
    };

    const renderMainItem = () => {
        if (parentItem && (!isLgBreakpointOrAbove ? index === 1 : index === 0)) {
            return (
                <Flex
                    flexDir="column"
                    flexWrap="nowrap"
                    flexBasis="100%"
                    mt={{ base: "sp16", lg: "0" }}
                    mb={"sp32"}
                >
                    <Box pt={["2", null, null, "sp12"]} pb={["2", null, null, "sp12"]}>
                        <Box>
                            <Heading
                                as="h2"
                                size="h4"
                                display={"flex"}
                                justifyContent={"space-between"}
                                alignItems={"center"}
                            >
                                <Box as="span" pr={{ lg: "sp16" }}>
                                    {getNavigationText(parentItem)}
                                </Box>
                                <BundleIcon
                                    name="ArrowForwardIos"
                                    width={"16px"}
                                    height={"16px"}
                                    size={"sm"}
                                    display={{ base: "none", lg: "block" }}
                                />
                            </Heading>
                            {navigationText && (
                                <Text mt={"sp16"} pr={{ lg: "sp16" }} size="body">
                                    {navigationText}
                                </Text>
                            )}
                        </Box>
                    </Box>
                </Flex>
            );
        }
        return null;
    };

    const renderParentItem = () => {
        if (parentItem && (!isLgBreakpointOrAbove ? index > 1 : index > 0)) {
            return (
                <Box
                    key={`${parentItem.Id}`}
                    className={`subnav__item${levelClass}${
                        parentItem.Href === router.asPath ? " subnav__item--active" : ""
                    }`}
                >
                    <Link
                        as={NextLink}
                        href={parentItem.Href}
                        ref={parentRef as never}
                        onKeyDown={onKeyDown}
                        onClick={closeSubNav}
                    >
                        <Text as="span" className="subnav__item--text">
                            {getNavigationText(parentItem)}
                        </Text>
                    </Link>
                </Box>
            );
        }
        return null;
    };

    const renderChildItem = (fields: MainNavigationFieldsProps) => {
        if (fields.Children && fields.Children?.length > 0) {
            const inPath: boolean =
                fields.Href === router.asPath ||
                activeMenuData.some((item) => item.fields === fields);
            let isInPathClass = "";
            if (activeMenuData.length === 2) {
                if (inPath) {
                    isInPathClass = " subnav__item--in-path";
                } else {
                    isInPathClass = " subnav__item--out-path";
                }
            }

            return (
                <Box
                    as="li"
                    key={`${fields.Id}`}
                    className={`subnav__item${levelClass}${isInPathClass}`}
                >
                    <Button
                        variant={"unstyled"}
                        onClick={() => openNextSubNavLevel(fields)}
                        justifyContent="flexStart"
                        aria-controls={fields.Id}
                        textAlign="left"
                        size="sm"
                        rightIcon={
                            <BundleIcon name="ArrowForwardIos" flex="0 0 auto" size={"sm"} />
                        }
                    >
                        <Text className="subnav__item--text" as="span" flex="1">
                            {getNavigationText(fields)}
                        </Text>
                    </Button>
                </Box>
            );
        }

        return (
            <Box
                as="li"
                key={`${fields.Id}`}
                className={`subnav__item${levelClass}${
                    fields.Href === router.asPath ? " subnav__item--active" : ""
                }`}
            >
                <Link as={NextLink} href={fields.Href} onClick={closeSubNav}>
                    {getNavigationText(fields)}
                </Link>
            </Box>
        );
    };

    const renderSubNavItems = () => {
        let flexBasis = "100%"; // Default value
        if (isLgBreakpointOrAbove) {
            flexBasis = index === 0 ? "66.6666%" : "33.3333%";
        }

        return (
            <MotionBox
                id={parentItem?.Id}
                animate={open ? "open" : "closed"}
                variants={motionVariants}
                initial="closed"
                transition={transition}
                onAnimationComplete={() => {
                    if (open) {
                        parentRef.current?.focus();
                    }
                }}
                w={["100%", null, null, "66.66%"]}
                className={`subnav__block${activeIndex >= 1 ? " active" : ""}`}
                aria-expanded={open}
                aria-label={String(
                    parentItem?.NavigationTitle.value ?? parentItem?.Title.value ?? "Home"
                )}
                flexBasis={flexBasis}
                gap={100}
            >
                <Box
                    display={["block", null, null, index === 0 ? "flex" : null]}
                    gap={100}
                    overflowY="auto"
                    width="100%"
                    zIndex="1"
                    opacity={
                        !isLgBreakpointOrAbove &&
                        ((index === 0 && (activeIndex === 1 || activeIndex === 2)) ||
                            (index === 1 && activeIndex === 2))
                            ? 0
                            : 1
                    }
                    transition="opacity 0.25s ease-in-out"
                >
                    {renderBackButton()}
                    {renderParentItem()}
                    {renderMainItem()}

                    <Flex as="ul" flexDir="column" flexWrap="nowrap" flexBasis="100%">
                        {Children?.map((fields: MainNavigationFieldsProps) =>
                            renderChildItem(fields)
                        )}
                    </Flex>
                </Box>
            </MotionBox>
        );
    };

    const openNextSubNavLevel = (fields: MainNavigationFieldsProps) => {
        if (index !== null) {
            handleLevelChange(index + 1, fields);
        }
    };

    return (
        <>
            {renderSubNavItems()}
            {Children?.map((fields: MainNavigationFieldsProps, subNavItemIndex: number) => {
                const updatedIndex = index + 1;
                const matchingMenuItem = activeMenuData.some((item) => item.fields === fields);

                if (fields.Children?.length) {
                    return (
                        <SubNavigationLevel
                            key={`subNavLevel_${fields.Id}`}
                            Children={fields.Children}
                            index={updatedIndex}
                            parentItem={fields}
                            open={matchingMenuItem}
                            disclosureProps={disclosureProps}
                            closeSubNav={closeSubNav}
                            handleLevelChange={handleLevelChange}
                            activeIndex={activeIndex}
                            activeMenuData={activeMenuData}
                            isLgBreakpointOrAbove={isLgBreakpointOrAbove}
                            subNavItemIndex={subNavItemIndex}
                        />
                    );
                }
                return null;
            })}
        </>
    );
};

export default SubNavigationLevel;
